import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { GetJob, UpdateJob } from '../../api/jobs-endpoint';
import SnackbarAlert from '../../components/Alerts';
import CancelButton from '../../components/CancelButton';
import JobRulesTabPanel from '../../components/jobs/JobRulesTabPanel';
import JobTabPanel from '../../components/jobs/JobTabPanel';
import JobVariablesTabPanel from '../../components/jobs/JobVariablesTabPanel';
import JobGroupsTabPanel from '../../components/jobs/JobGroupsTabPanel';
import useConfigStyles from '../../components/useConfigStyles';
import SubmitButton from '../../components/SubmitButton';
import { getLinkToJob } from '../../navigator/links';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useJobsContext } from '../../stores/jobs-context';
import { Job } from '../../types/job';
import { Rule } from '../../types/rule';
import { Variable } from '../../types/variable';
import { Group } from '../../types/group';
import { defaultJob } from '../../types/defaultValues';

function JobEditPage(): JSX.Element {
  const [job, setJob] = useState<Job>(defaultJob);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [notify, toggleNotification] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const { jobName } = useParams<{ jobName: string }>();
  const classes = useConfigStyles();
  const navigate = useNavigate();
  const jobsContext = useJobsContext();

  const fetchJob = useCallback(async () => {
    if (jobName) {
      const response = await GetJob(jobName);
      if (response.success && response.data) {
        setJob(response.data);
      } else {
        setResponseMessage(response.message || '');
      }
      setFetched(response.success);
    }
  }, [jobName]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Prevent submit non numeric value
    if (Number.isNaN(job.RowDataLimit)) {
      job.RowDataLimit = 1;
    }
    const response = await UpdateJob(jobName!, job);
    if (response.success) {
      setResponseMessage('Success');
      jobsContext.fetchData();
      navigate(getLinkToJob(job.Name));
    } else {
      setResponseMessage(response.message || '');
      setErrors(response.errors || {});
      toggleNotification(true);
    }
  };

  const handleTabChange = (event: ChangeEvent<unknown>, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    toggleNotification(false);
  };

  const handleCancelClick = () => {
    navigate(getLinkToJob(jobName!));
  };

  const cleanErrors = (name: string) => {
    if (errors[name]) {
      delete errors[name];
    }
  };

  const handleJobUpdate = (updatedJob: Job, fieldName: string) => {
    setJob(updatedJob);
    cleanErrors(fieldName);
  };

  const handleVariablesUpdate = (variables: Variable[]) => {
    setJob({ ...job, Variables: variables });
  };

  const handleRulesUpdate = (rules: Rule[]) => {
    setJob({ ...job, Rules: rules });
  };

  const handleGroupsUpdate = (groups: Group[]) => {
    setJob({ ...job, Groups: groups });
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Paper elevation={3} className={classes.paper}>
        <Tabs value={tabValue} indicatorColor='primary' textColor='primary' onChange={handleTabChange} centered className={classes.tabs} variant='fullWidth'>
          <Tab label='Job' />
          <Tab label='Job Variables' />
          <Tab label='Job Rules' />
          <Tab label='Job Groups' />
        </Tabs>
        <Card>
          <CardContent>
            <JobTabPanel job={job} index={0} selectedTab={tabValue} editable onJobUpdate={handleJobUpdate} errors={errors} />
            <JobVariablesTabPanel schoolYear={job.SchoolYear} variables={job.Variables} index={1} selectedTab={tabValue} editable onVariablesUpdate={handleVariablesUpdate} />
            <JobRulesTabPanel rules={job.Rules} index={2} selectedTab={tabValue} editable onRulesUpdate={handleRulesUpdate} />
            <JobGroupsTabPanel groups={job.Groups} index={3} selectedTab={tabValue} editable onGroupsUpdate={handleGroupsUpdate} />
          </CardContent>
          <CardActions style={{'marginBottom': '24px'}}>
            <Grid container alignItems='center' justifyContent='center' direction='row' spacing={5}>
              <Grid item>
                  <CancelButton label='Cancel' className={classes.button} onClick={handleCancelClick} />
              </Grid>
              <Grid item>
                  <SubmitButton className={classes.button} label='Update Job' />
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Paper>
      <SnackbarAlert open={notify} message={responseMessage} handleClose={handleCloseSnackbar} />
    </form>
  );
}

export default JobEditPage;
